// @flow
import * as React from 'react'
import { connectAutoComplete } from 'react-instantsearch-dom'
import AutoSuggest from 'react-autosuggest'
import styled from 'styled-components'
import { colors } from '../shared/colors'
import type { TrackType } from '../../flowTypes'
import { GoodSansB2 } from '../shared/typography'
import SeriesIcon from '@components/shared/icons/SeriesIcon'
import TagIcon from '@components/shared/icons/TagIcon'
import StoriesIcon from '@components/shared/icons/StoriesIcon'

type HitType = {
  index: 'tags' | 'tracks' | 'categories' | 'series',
  hits: TrackType[]
}
type HitsType = HitType[]
type PropsType = {
  hits: any,
  refine: any,
  currentRefinement: string,
  searchActive: boolean,
  selectTag: (id: ?string) => void,
  selectCategory: (id: ?string) => void,
  selectSeries: (id: ?string) => void
}
type SuggestionTagType = {|
  name: string,
  objectID: string,
  slugs: {
    [slug: string]: 1
  }
|}
type AlgoliaTrackType = TrackType & {|
  objectID: string,
  slugs: {
    [key: string]: 1
  },
  display_name: string,
  subType: string,
  description: string,
  name: string
|}
type AlgoliaCategoryType = {|
  display_name: string,
  objectID: string,
  subType: string,
  id: string,
  description: string
|}
type AlgoliaSeriesType = {|
  name: string,
  objectID: string,
  id: string,
  description: string
|}
type SubSuggestionType =
  | SuggestionTagType
  | AlgoliaTrackType
  | AlgoliaCategoryType
  | AlgoliaSeriesType
type SuggestionSelectedType = {
  method: 'click' | 'enter',
  sectionIndex: number,
  suggestionValue: string,
  suggestionIndex: number,
  suggestion: SubSuggestionType
}

class AutoComplete extends React.PureComponent<
  PropsType,
  { value?: string, suggestions?: any[] }
> {
  constructor (props: PropsType): void {
    super(props)
    this.state = {
      value: this.props.currentRefinement,
      suggestions: []
    }
    this.renderSuggestion = this.renderSuggestion.bind(this)
  }

  componentDidUpdate (prevProps: any, _prevState: any, _snapshot: any): void {
    if (this.props.hits !== prevProps.hits) {
      const newHits = this.filterHits(this.props.hits)
      this.setState({
        suggestions: newHits,
        value: this.props.currentRefinement
      })
    }
  }

  filterHits (hits: HitsType): HitsType {
    const newHits = []
    let tagHits = {}
    let trackHits = {}
    let categoryHits = {}
    let seriesHits = {}
    hits.forEach((hit: HitType): void => {
      if (hit.index === 'tags') {
        tagHits = hit
      } else if (hit.index === 'categories') {
        categoryHits = hit
      } else if (hit.index === 'series') {
        seriesHits = hit
      } else if (hit.index === 'tracks') {
        trackHits = {
          index: 'tracks',
          hits: hit.hits
        }
      }
    })
    if (Object.keys(tagHits).length) {
      newHits.push(tagHits)
    }
    if (Object.keys(categoryHits).length) {
      newHits.push(categoryHits)
    }
    if (Object.keys(seriesHits).length) {
      newHits.push(seriesHits)
    }
    if (Object.keys(trackHits).length) {
      newHits.push(trackHits)
    }
    return newHits
  }

  onChange = (event: any, { newValue }: any): void => {
    this.setState({
      value: newValue
    })
  }

  onSuggestionsFetchRequested = ({ value }: any): void => {
    this.props.refine(value)
  }

  onSuggestionsClearRequested = (): void => {
    this.setState({
      suggestions: []
    })
  }

  onSuggestionSelected = (
    event: any,
    suggestion: SuggestionSelectedType
  ): void => {
    const value = suggestion.suggestionValue || this.state.value
    if (
      suggestion.suggestion &&
      suggestion.suggestion.slugs &&
      suggestion.sectionIndex === 0
    ) {
      this.props.selectTag((suggestion.suggestion || {}).objectID)
    } else if (
      suggestion.suggestion &&
      suggestion.suggestion.subType &&
      suggestion.suggestion.display_name
    ) {
      this.props.selectCategory((suggestion.suggestion || {}).objectID)
    } else if (
      suggestion.suggestion &&
      (suggestion.suggestion || {}).name &&
      suggestion.suggestion.description
    ) {
      this.props.selectSeries((suggestion.suggestion || {}).objectID)
    }
    this.setState({
      value,
      suggestions: []
    })
  }

  getSuggestionValue (hit: any): any {
    if (hit) {
      return hit.name || hit.title || hit.display_name
    }
    return null
  }

  renderSuggestion = (hit: any): React.Node => {
    if (hit) {
      if (hit.title2 || hit.title) {
        return (
          <HitContainer>
            <IconContainer>
              <StoriesIcon
                size={16}
                color={colors.white}
              />
            </IconContainer>
            <p>{hit.title2 || hit.title}</p>
          </HitContainer>
        )
      }
      if (hit.display_name) {
        // check for hunk, use star
        return (
          <HitContainer>
            <IconContainer>
              <TagIcon
                size={16}
                color={colors.white}
              />
            </IconContainer>
            <p>{hit.display_name}</p>
          </HitContainer>
        )
      }
      if (hit.name) {
        if (!hit.slugs) {
          return (
            <HitContainer>
              <IconContainer>
                <SeriesIcon
                  size={16}
                  color={colors.white}
                />
              </IconContainer>
              <p>{hit.name}</p>
            </HitContainer>
          )
        } else {
          return (
            <HitContainer>
              <IconContainer>
                <TagIcon
                  size={16}
                  color={colors.white}
                />
              </IconContainer>
              <p>{hit.name}</p>
            </HitContainer>
          )
        }
      }
    }
    return null
  }

  renderSectionTitle (section: any): any {
    return null
  }

  getSectionSuggestions (section: any): any {
    if (section?.hits) {
      return section.hits
    }
    return []
  }

  render (): React.Node {
    const { hits } = this.props
    const { value } = this.state
    const inputProps = {
      'aria-label': 'Search tags, stories, and more in the Dipsea library',
      placeholder: 'Scottish Accent, WLW, Fantasy...',
      onChange: this.onChange,
      value: value || ''
    }
    const hasStoryHits = hits.filter((hit: any): boolean => hit.hits.length).length > 0

    return (
      <Container>
        <AutoSuggest
          highlightFirstSuggestion={true}
          suggestions={this.state.suggestions}
          multiSection={true}
          onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
          onSuggestionsClearRequested={this.onSuggestionsClearRequested}
          onSuggestionSelected={this.onSuggestionSelected}
          getSuggestionValue={this.getSuggestionValue}
          renderSuggestion={this.renderSuggestion}
          inputProps={inputProps}
          renderSectionTitle={this.renderSectionTitle}
          getSectionSuggestions={this.getSectionSuggestions}
        />

        {!hasStoryHits && (
          <EmptySearchContainer>
            <p>{`Hmmm... we're coming up empty.`}</p>
          </EmptySearchContainer>
        )}
      </Container>
    )
  }
}

export default connectAutoComplete(AutoComplete)

const Container = styled.div`
    li {
        &[aria-selected='true'] {
            button {
                background-color: ${colors.white10};
            }
        }
    }
`
const EmptySearchContainer = styled.div`
    margin: 0;
    padding: 20px 40px;
    p {
        ${GoodSansB2};
    }
`
const HitContainer = styled.button`
    align-items: center;
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    outline: none;
    width: 100%;
    p {
        ${GoodSansB2};
        padding: 0.5em 10px 0.5em 0;
    }
`
const IconContainer = styled.div`
  width: 34px ;
`
